import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { Base64 } from "js-base64";
import { FileEntry } from "model";
import { useEffect, useState } from "react";
import { getSingleFileUploadViaFileEntryApi } from "services";
import { downloadFileHelper } from "./downloadFileHelper";

export function isPdf(fileName?: string | null): boolean {
  return !!fileName && fileName.toLowerCase().endsWith(".pdf");
}

export function isImage(fileName?: string | null): boolean {
  if (!fileName) return false;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName);
}

interface Props {
  open: boolean;
  file: FileEntry | null;
  onClose: () => void;
}

/**
 * Loads the underlying file via the existing FileEntry → FileUpload API
 * (base64 over JSON) and renders it inside an iframe blob URL. Supports
 * PDF + image MIME types; everything else still shows but the browser
 * decides how to render it.
 */
export default function FilePreviewDialog({ open, file, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const { notify } = useNotification();

  useEffect(() => {
    if (!open || !file) return;
    let cancelled = false;
    setLoading(true);
    setBlobUrl(null);
    let createdUrl: string | null = null;

    void (async () => {
      try {
        const fileUpload = await getSingleFileUploadViaFileEntryApi({
          guid: file.guid,
        });
        if (cancelled) return;
        const data = Base64.toUint8Array(fileUpload.file);
        const name: string =
          file.fileName ?? (fileUpload.fileName as string | undefined) ?? "";
        const mimeType = isPdf(name)
          ? "application/pdf"
          : isImage(name)
          ? `image/${name
              .split(".")
              .pop()
              ?.toLowerCase()
              .replace("jpg", "jpeg")}`
          : "application/octet-stream";
        const blob = new Blob([data], { type: mimeType });
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch {
        if (!cancelled) notify("Error loading file", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, file, notify]);

  const handleClose = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mr: 2,
          }}
        >
          {file?.fileName || file?.fileDescription || "File"}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={() => {
                if (file) void downloadFileHelper(file.guid, notify);
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : blobUrl ? (
          <iframe
            src={blobUrl}
            title={file?.fileName || "File viewer"}
            style={{ flex: 1, width: "100%", border: "none" }}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">
              Unable to load file preview
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
